import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Tilt from 'react-parallax-tilt';
import { 
  CloudArrowUpIcon,
  SparklesIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import NET from 'vanta/dist/vanta.net.min';

function StepCard({ icon: Icon, title, description, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Tilt tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.05}>
        <div className="glass p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full min-h-[300px] flex flex-col items-center justify-center">
          <div className="relative flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-16 h-16 flex items-center justify-center glow">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <span className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full text-white text-lg font-bold flex items-center justify-center">
              {index + 1}
            </span>
          </div>
          <h3 className="text-2xl font-semibold mb-4 text-white text-center">{title}</h3>
          <p className="text-white/80 text-center text-lg">{description}</p>
        </div>
      </Tilt>
    </motion.div>
  );
}

function GetStarted() {
  const [vantaEffect, setVantaEffect] = React.useState(null);
  const vantaRef = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!vantaEffect) {
      setVantaEffect(
        NET({
          el: vantaRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          color: 0x3b82f6,
          backgroundColor: 0x0a1930,
          points: 20.00,
          maxDistance: 15.00,
          spacing: 15.00,
        })
      );
    }
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  const steps = [
    {
      icon: CloudArrowUpIcon,
      title: "Upload Your Image",
      description: "Select and upload the image you want to process. We support JPG, PNG, and WEBP formats."
    },
    {
      icon: SparklesIcon,
      title: "AI Processing",
      description: "Our advanced AI model will automatically detect and remove the background from your image."
    },
    {
      icon: ArrowDownTrayIcon,
      title: "Download Result",
      description: "Download your processed image with the background removed, ready to use in your projects."
    },
    {
      icon: ArrowPathIcon,
      title: "Process More",
      description: "Continue processing more images or try different settings to get the perfect result."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1E40AF] to-[#1E3A8A] text-white" ref={vantaRef}>
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Get Started</h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Remove image backgrounds in just a few simple steps using our powerful AI technology.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <StepCard key={index} {...step} index={index} />
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center"
        >
          <div className="glass p-8 rounded-modern max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-white">Ready to Start?</h2>
            <p className="text-white/80 mb-8">
              Try our AI-powered background removal tool now and see the magic happen!
            </p>
            <button 
              onClick={() => navigate('/')} 
              className="py-3 px-8 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white rounded-modern hover:opacity-90 transition-all duration-300 text-lg font-medium"
            >
              Try It Now
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default GetStarted;
